import type { Step } from '@eip/engine';
import type { TemplateStatus } from '../state/mockup';
import type { DocEntry, Settings } from '../state/store';
import { formatExportError } from './download';
import { addFilmsToZip, separationReadiness, slug, type FilmsDeps } from './films';
import { exportFileName } from './format';
import { addMockupsToZip, hasMockup, mockupReport, type MockupDeps } from './mockups';
import { buildReport, reportJson } from './report';
import { reportHtml } from './report-html';
import type { TemplateSource } from './templates';
import type { MockupFormat } from '../worker/protocol';
import { createZip, uniqueName } from './zip';

export interface BatchDeps {
  exportPng(id: string): Promise<{ bytes: Uint8Array; width: number; height: number; dpi: number }>;
  /** Re-read a document from the live store. The batch snapshots each doc's run
   *  generation at click time and compares it against this right before the
   *  encode, so a doc edited (or closed) mid-batch is skipped instead of landing
   *  in an archive whose reports describe the click-time state. */
  getDoc(id: string): DocEntry | undefined;
  onProgress?(done: number, total: number): void;
  now?: Date;
  /** Set in screen-print mode: every ready doc contributes its film package
   *  (`<name>/…`) instead of a DTF PNG, and a doc with no built separation is
   *  skipped rather than exported the other way. */
  films?: { exportFilms: FilmsDeps['exportFilms'] };
  /** Set in mockup mode: every ready doc contributes its colourways
   *  (`<name>/mockups/…`) instead of a DTF PNG, and a doc with no mockup set up
   *  is skipped rather than exported the other way. */
  mockups?: {
    sources: TemplateSource[]; deps: MockupDeps; format: MockupFormat;
    /** The scenes the store holds, so the ticket's Backdrop line can name one rather than print a
     *  UUID. Optional: without it a scene backdrop reads as missing, which is what it is to anyone
     *  reading the ticket without the shop's own browser storage. */
    scenes?: ReadonlyArray<{ id: string; name: string }>;
    /** The prepared blanks, for the same reason the single-document ticket and the inspector take
     *  it: a backdrop is drawn only where the blank's mask separates from what it was photographed
     *  against, and a row that names one the render ignored describes a file that is not in the
     *  archive beside it. Optional, as everywhere else — without it a backdrop reads as applied. */
    templateStatus?: Record<string, TemplateStatus>;
  };
}
/** Why one file is not in the archive: not prepared, changed under the batch, or its encode failed. */
export interface SkippedDoc { name: string; reason: string }
export interface BatchResult { blob: Blob; filename: string; exported: string[]; skipped: SkippedDoc[] }

/** The docs a batch will attempt: prepared, not mid-run, and not a failed run. */
export function isBatchReady(d: DocEntry): boolean { return !!d.run && !d.runError && !d.running; }

const two = (n: number) => String(n).padStart(2, '0');
export function batchZipName(now: Date): string {
  return `easyimageprep_${now.getFullYear()}${two(now.getMonth() + 1)}${two(now.getDate())}-${two(now.getHours())}${two(now.getMinutes())}.zip`;
}

/**
 * Encode one document at a time (a 5225² export is ~20 MB of PNG, and the
 * worker holds one full-res filter buffer per encode), but every encoded PNG
 * stays on the main thread: its bytes are retained in the ZIP writer's chunk
 * list until `finish()`, which then concatenates them into one Blob. Peak main
 * thread memory is therefore roughly twice the batch's total PNG size — a
 * 20-file batch of 5225² exports is ~400 MB held, ~800 MB at the Blob copy.
 * Streaming to disk (`showSaveFilePicker`) is the fix when that ceiling bites.
 *
 * Consistency: the reports are built from the click-time `docs` snapshot, so
 * every doc is re-read from the store (`deps.getDoc`) immediately before its
 * encode and skipped if it is gone, is mid-run, or its `run.generation` moved.
 * Edits stay live during a batch; what changes under it is simply left out,
 * with a reason.
 */
export async function exportAll(docs: ReadonlyArray<DocEntry>, steps: Step[], settings: Settings, deps: BatchDeps): Promise<BatchResult> {
  const now = deps.now ?? new Date();
  const ready = docs.filter(isBatchReady);
  const readyIds = new Set(ready.map((d) => d.id));
  const skipped: SkippedDoc[] = docs
    .filter((d) => !readyIds.has(d.id))
    .map((d) => ({ name: d.name, reason: d.runError ? `run failed: ${d.runError}` : d.running ? 'still running' : 'not prepared yet' }));
  /** The run each doc was showing when the batch started; anything else is a different image. */
  const generations = new Map(ready.map((d) => [d.id, d.run?.generation]));
  const zip = createZip();
  const taken = new Set<string>(['report.html', 'report.json']);
  const exported: string[] = [];
  for (let i = 0; i < ready.length; i++) {
    const d = ready[i];
    const live = deps.getDoc(d.id);
    if (!live) skipped.push({ name: d.name, reason: 'closed' });
    // `running` as well as the generation: `run.generation` only moves when a
    // run RESULT lands, while `running` is set the moment a run is REQUESTED
    // (runner.ts). Between the two, an edited doc still shows its old, matching
    // generation — and `exportPng` queues behind the in-flight run in the same
    // worker, so it would encode the POST-edit pixels and label them with the
    // pre-edit state. A doc mid-run is never part of this archive.
    else if (live.running) skipped.push({ name: d.name, reason: 're-running during export' });
    else if (!live.run || live.run.generation !== generations.get(d.id)) skipped.push({ name: d.name, reason: 'changed during export' });
    // A document nobody has set a mockup up on (no template chosen, or the blank
    // it named has left the registry) has nothing to render into.
    else if (deps.mockups && !hasMockup(live, deps.mockups.sources)) skipped.push({ name: d.name, reason: 'no mockup set up' });
    else if (deps.films && separationReadiness(live) !== 'ok') {
      // Two different problems, two different fixes: build it, or rebuild it
      // against the run this doc is showing now.
      const readiness = separationReadiness(live);
      skipped.push({ name: d.name, reason: readiness === 'stale' ? 'separation is stale' : 'no separation built' });
    } else {
      try {
        if (deps.mockups) {
          // Same folder rule as the films path: a slugged, de-duplicated path
          // component, with the colourways under `mockups/` inside it.
          const folder = uniqueName(slug(live.name.replace(/\.[^.]+$/, '')) || 'job', taken);
          const { sources, format } = deps.mockups;
          // The batch's own `onProgress` counts DOCUMENTS, so the per-colourway one
          // is deliberately not forwarded; `ensureTemplate` and `ensureScene` are —
          // every blank a colourway lands on, and the backdrop scene it names, has
          // to be in the worker before it is rendered.
          const { mockupExport, mockupSheet, ensureTemplate, ensureScene } = deps.mockups.deps;
          const { count, skipped: colours } = await addMockupsToZip(
            zip, live, sources, { mockupExport, mockupSheet, ensureTemplate, ensureScene, now }, `${folder}/mockups`, format,
          );
          // A colour no blank of the style can wear is named, whether or not the
          // rest of the document made it in.
          for (const reason of colours) skipped.push({ name: d.name, reason });
          if (count === 0) skipped.push({ name: d.name, reason: 'no colourway could be rendered' });
          else exported.push(folder);
        } else if (deps.films) {
          // The live doc, not the click-time snapshot: its `builtGeneration` is
          // the build the worker still holds films for.
          // `slug` rather than the doc's own base name: a folder is a path
          // component, and `Art: v2?.png` would produce an entry Windows'
          // extractor refuses. It also strips the dots that would otherwise make
          // `uniqueName` (which splits on the last one) turn a colliding
          // `logo.v2` into `logo-2.v2`.
          const folder = uniqueName(slug(live.name.replace(/\.[^.]+$/, '')) || 'job', taken);
          await addFilmsToZip(zip, live, steps, settings, { exportFilms: deps.films.exportFilms, now }, folder);
          exported.push(folder);
        } else {
          const { bytes, width, height, dpi } = await deps.exportPng(d.id);
          const name = uniqueName(exportFileName(d.name, settings.garmentHex, width, height, dpi), taken);
          zip.add(name, bytes, { mtime: now });
          exported.push(name);
        }
      } catch (e) {
        // One doc's failure (closed mid-encode, worker error) must not throw
        // away every PNG already in the archive after minutes of encoding.
        // A films failure can leave that doc's folder half-written; it is named
        // in `skipped`, so the operator knows not to hand that folder over.
        skipped.push({ name: d.name, reason: formatExportError(e) });
      }
    }
    deps.onProgress?.(i + 1, ready.length);
  }
  // Nothing got in: there is no deliverable to hand over, so report it as a
  // failure with the reasons rather than downloading a reports-only archive.
  if (exported.length === 0 && ready.length > 0) {
    throw new Error(`nothing exported — ${skipped.map((s) => `${s.name} (${s.reason})`).join(', ')}`);
  }
  // The top-level ticket describes what the archive holds, so in mockup mode it
  // carries the colourway rows too (no thumbnails: they are one worker render
  // each, and the batch has already paid for every one of these files).
  // `docs` again as the last argument: an extra print names another document, and the ticket can
  // only call it by name while that document is one of these.
  // `templateStatus` on the same terms as the single-document ticket: by the time this runs
  // `prepareAll` has put every blank the plans name through `ensureTemplate`, so the statuses are
  // there — and a row on a non-separable blank reads `none` rather than naming a backdrop the
  // composite dropped.
  const report = buildReport(docs, steps, settings, now,
    deps.mockups && ((d) => mockupReport(d, deps.mockups!.sources, deps.mockups!.format, deps.mockups!.templateStatus, docs, deps.mockups!.scenes)));
  const enc = new TextEncoder();
  zip.add('report.html', enc.encode(reportHtml(report)), { compress: true, mtime: now });
  zip.add('report.json', enc.encode(reportJson(report)), { compress: true, mtime: now });
  return { blob: await zip.finish(), filename: batchZipName(now), exported, skipped };
}
